import { useMemo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  TrendingUp, 
  Building2, 
  CreditCard, 
  DollarSign, 
  Wallet,
  Target,
  Trophy,
  ArrowRight,
  Plus
} from 'lucide-react'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { PageHeader } from '@/components/layout'
import { StatCard } from '@/components/shared'
import { LevelBadge, StreakIndicator } from '@/components/gamification'
import { GoalTypeIcon } from '@/components/goals'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import { visitPage } from '@/store/slices/gamificationSlice'
import { formatCurrency, formatPercentage } from '@/lib/utils'
import { useInflation } from '@/contexts/InflationContext'
import { calculateGoalProgress } from '@/lib/achievements'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
)

export default function Dashboard() {
  const dispatch = useAppDispatch()
  const portfolio = useAppSelector(state => state.portfolio)
  const realEstate = useAppSelector(state => state.realEstate)
  const debt = useAppSelector(state => state.debt)
  const budget = useAppSelector(state => state.budget)
  const retirement = useAppSelector(state => state.retirement)
  const goals = useAppSelector(state => state.goals)
  const gamification = useAppSelector(state => state.gamification)
  const { showRealReturns, calculateRealValue, calculateRealReturn } = useInflation()

  // Track page visit for explorer achievement
  useEffect(() => {
    dispatch(visitPage('dashboard'))
  }, [dispatch])

  // Get top 3 active goals by priority
  const topGoals = useMemo(() => {
    return [...goals.goals]
      .filter(g => g.status === 'active')
      .sort((a, b) => a.priority - b.priority)
      .slice(0, 3)
  }, [goals.goals])

  // Calculate totals
  const totalDebtBalance = debt.totalBalance + realEstate.totalMortgageDebt
  const totalNetWorth = portfolio.totalValue + realEstate.totalValue - totalDebtBalance
  const monthlyFreeCashFlow = budget.calculations.freeCashFlow / 12

  // Calculate retirement success rate
  const retirementSuccessRate = useMemo(() => {
    const yearsToRetirement = retirement.retirementAge - retirement.currentAge
    const yearsInRetirement = retirement.lifeExpectancy - retirement.retirementAge
    
    if (yearsToRetirement <= 0 || yearsInRetirement <= 0) return 0
    
    const totalAnnualContributions = Object.values(budget.retirementContributions)
      .reduce((sum, contrib) => sum + contrib.amount, 0)
    
    let portfolioValue = retirement.currentSavings
    for (let year = 0; year < yearsToRetirement; year++) {
      const effectiveReturn = showRealReturns 
        ? calculateRealReturn(retirement.expectedReturn) / 100
        : retirement.expectedReturn / 100
      portfolioValue = portfolioValue * (1 + effectiveReturn) + totalAnnualContributions
    }
    
    const inflationAdjustedIncome = retirement.desiredIncome * Math.pow(1 + retirement.inflationRate / 100, yearsToRetirement)
    const requiredPortfolio = (inflationAdjustedIncome - retirement.socialSecurityIncome) * yearsInRetirement
    
    return requiredPortfolio > 0 ? Math.min(100, (portfolioValue / requiredPortfolio) * 100) : 100
  }, [retirement, budget.retirementContributions, showRealReturns, calculateRealReturn])

  // Generate chart data
  const chartData = useMemo(() => {
    const years = 30
    const labels: string[] = []
    const netWorthData: number[] = []
    
    let runningPortfolio = portfolio.totalValue || 0
    let runningRetirement = retirement.currentSavings || 0
    
    const effectiveReturn = showRealReturns 
      ? calculateRealReturn(7) / 100 
      : 0.07
    const monthlyReturn = Math.pow(1 + effectiveReturn, 1/12) - 1
    
    for (let year = 0; year <= years; year++) {
      labels.push(`Year ${year}`)
      
      // Simulate growth
      for (let m = 0; m < 12; m++) {
        runningPortfolio *= (1 + monthlyReturn)
        runningRetirement *= (1 + monthlyReturn)
      }
      
      const futureRealEstateValue = realEstate.totalValue * Math.pow(1.03, year)
      const remainingDebt = Math.max(0, totalDebtBalance - (debt.totalMonthlyPayments * 12 * year))
      
      const totalNetWorthYear = runningPortfolio + runningRetirement + futureRealEstateValue - remainingDebt
      netWorthData.push(showRealReturns ? calculateRealValue(totalNetWorthYear, year) : totalNetWorthYear)
    }
    
    return { labels, netWorthData }
  }, [portfolio, retirement, realEstate, debt, totalDebtBalance, showRealReturns, calculateRealValue, calculateRealReturn])

  const lineChartData = {
    labels: chartData.labels,
    datasets: [
      {
        label: 'Projected Net Worth',
        data: chartData.netWorthData,
        borderColor: 'hsl(var(--chart-2))',
        backgroundColor: 'hsla(var(--chart-2), 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: function(context: { parsed: { y: number | null } }) {
            return formatCurrency(context.parsed.y ?? 0)
          },
        },
        backgroundColor: 'hsl(var(--popover))',
        titleColor: 'hsl(var(--popover-foreground))',
        bodyColor: 'hsl(var(--popover-foreground))',
        borderColor: 'hsl(var(--border))',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          color: 'hsl(var(--muted-foreground))',
          maxTicksLimit: 6,
        },
      },
      y: {
        display: true,
        grid: {
          color: 'hsl(var(--border))',
        },
        ticks: {
          color: 'hsl(var(--muted-foreground))',
          callback: function(value: number | string) {
            return formatCurrency(Number(value))
          },
        },
      },
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false,
    },
  }

  const quickActions = [
    { label: 'Add Investment', icon: TrendingUp, href: '/portfolio' },
    { label: 'Add Property', icon: Building2, href: '/real-estate' },
    { label: 'Add Debt', icon: CreditCard, href: '/debt' },
    { label: 'Update Budget', icon: Wallet, href: '/budget' },
  ]

  const insights = [
    {
      title: 'Debt-to-Income',
      value: budget.calculations.grossIncome > 0 
        ? formatPercentage((debt.totalMonthlyPayments * 12 / budget.calculations.grossIncome) * 100)
        : '0%',
      status: budget.calculations.grossIncome > 0 && (debt.totalMonthlyPayments * 12 / budget.calculations.grossIncome) * 100 > 43 ? 'warning' : 'good',
      description: 'Keep under 43% for healthy finances'
    },
    {
      title: 'Savings Rate',
      value: formatPercentage(budget.calculations.savingsRate),
      status: budget.calculations.savingsRate >= 20 ? 'good' : budget.calculations.savingsRate >= 10 ? 'warning' : 'bad',
      description: 'Target 20%+ for financial independence'
    },
    {
      title: 'Emergency Fund',
      value: budget.calculations.totalExpenses > 0 
        ? `${Math.round((portfolio.totalValue * 0.1) / (budget.calculations.totalExpenses / 12))} mo`
        : 'N/A',
      status: 'neutral',
      description: 'Aim for 3-6 months of expenses'
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Financial Dashboard" 
        description="Welcome back! Here's an overview of your financial health."
      />

      {/* Main Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Net Worth"
          value={formatCurrency(totalNetWorth)}
          change={totalNetWorth >= 0 ? '+4.5%' : undefined}
          changeType={totalNetWorth >= 0 ? 'positive' : 'negative'}
          icon={DollarSign}
          href="/portfolio"
        />
        <StatCard
          title="Portfolio Value"
          value={formatCurrency(portfolio.totalValue)}
          change="+5.2%"
          changeType="positive"
          icon={TrendingUp}
          href="/portfolio"
        />
        <StatCard
          title="Real Estate Equity"
          value={formatCurrency(realEstate.totalEquity)}
          change="+2.1%"
          changeType="positive"
          icon={Building2}
          href="/real-estate"
        />
        <StatCard
          title="Total Debt"
          value={formatCurrency(totalDebtBalance)}
          change="-1.8%"
          changeType="negative"
          icon={CreditCard}
          href="/debt"
        />
        <StatCard
          title="Monthly Free Cash"
          value={formatCurrency(monthlyFreeCashFlow)}
          changeType={monthlyFreeCashFlow > 0 ? 'positive' : 'negative'}
          change={monthlyFreeCashFlow > 0 ? 'Positive' : 'Negative'}
          icon={Wallet}
          href="/budget"
        />
        <StatCard
          title="Retirement Progress"
          value={formatPercentage(retirementSuccessRate)}
          changeType={retirementSuccessRate >= 100 ? 'positive' : 'neutral'}
          change={retirementSuccessRate >= 100 ? 'On Track' : 'Needs Work'}
          icon={Target}
          href="/retirement"
        />
      </div>

      {/* Chart and Quick Actions */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Net Worth Projection Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Net Worth Projection</CardTitle>
            <CardDescription>30-year projection based on current trajectory</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <Line data={lineChartData} options={chartOptions} />
            </div>
            <div className="mt-4 grid grid-cols-3 gap-4 border-t pt-4">
              <div>
                <p className="text-sm text-muted-foreground">Current</p>
                <p className="text-lg font-semibold">{formatCurrency(totalNetWorth)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">30 Year Projection</p>
                <p className="text-lg font-semibold text-success">{formatCurrency(chartData.netWorthData[30] || 0)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Growth</p>
                <p className="text-lg font-semibold text-success">
                  {formatCurrency((chartData.netWorthData[30] || 0) - totalNetWorth)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>Common tasks</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {quickActions.map((action) => (
              <Button
                key={action.label}
                variant="outline"
                className="w-full justify-start"
                asChild
              >
                <Link to={action.href}>
                  <action.icon className="mr-2 h-4 w-4" />
                  {action.label}
                </Link>
              </Button>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Goals & Gamification Row */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Goals Progress */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Goals Progress
              </CardTitle>
              <CardDescription>
                {goals.activeGoalsCount} active goal{goals.activeGoalsCount !== 1 && 's'}
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to="/goals">
                View All
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {topGoals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Target className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No active goals yet</p>
                <Button asChild>
                  <Link to="/goals">
                    <Plus className="mr-2 h-4 w-4" />
                    Create Your First Goal
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {topGoals.map((goal) => {
                  const progress = calculateGoalProgress(goal)
                  return (
                    <div key={goal.id} className="flex items-center gap-4">
                      <GoalTypeIcon type={goal.type} size="md" showBackground />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium truncate">{goal.name}</p>
                          <span className="text-sm text-muted-foreground">
                            {progress.toFixed(0)}%
                          </span>
                        </div>
                        <Progress value={progress} className="h-2" />
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-muted-foreground">
                            {formatCurrency(goal.currentAmount)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatCurrency(goal.targetAmount)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
                {goals.activeGoalsCount > 3 && (
                  <p className="text-sm text-muted-foreground text-center">
                    +{goals.activeGoalsCount - 3} more goal{goals.activeGoalsCount - 3 !== 1 && 's'}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Gamification Widget */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-primary" />
                Your Progress
              </CardTitle>
              <CardDescription>Achievements & streaks</CardDescription>
            </div>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/achievements">
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Level */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Level</span>
              <LevelBadge level={gamification.level} />
            </div>
            
            {/* XP */}
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total XP</span>
                <span className="font-medium">{gamification.totalXP.toLocaleString()}</span>
              </div>
            </div>
            
            {/* Streak */}
            <div className="pt-2 border-t">
              <StreakIndicator
                currentStreak={gamification.currentStreak}
                longestStreak={gamification.longestStreak}
                lastActivityDate={gamification.lastActivityDate}
                variant="compact"
              />
            </div>
            
            {/* Achievements Preview */}
            <div className="pt-2 border-t">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Achievements</span>
                <span className="text-sm font-medium">
                  {Object.values(gamification.achievements).filter(a => a.unlockedAt).length}/
                  {Object.values(gamification.achievements).length}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Financial Insights */}
      <Card>
        <CardHeader>
          <CardTitle>Financial Insights</CardTitle>
          <CardDescription>Key metrics to monitor your financial health</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            {insights.map((insight) => (
              <div key={insight.title} className="rounded-lg border p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">{insight.title}</span>
                  <Badge 
                    variant={
                      insight.status === 'good' ? 'success' : 
                      insight.status === 'warning' ? 'warning' : 
                      insight.status === 'bad' ? 'destructive' : 
                      'secondary'
                    }
                  >
                    {insight.status === 'good' ? 'Good' : 
                     insight.status === 'warning' ? 'Watch' : 
                     insight.status === 'bad' ? 'Improve' : 
                     'Info'}
                  </Badge>
                </div>
                <p className="text-2xl font-bold">{insight.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{insight.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
